import { displayUserName } from "@modules/user/deletedUser";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Database,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  MoistureLevel,
  PesTestDocument,
  PredictionPipeline,
  Season,
  SimpleSoilTexture,
  SortEnumType,
  UpdatePesTestCharacteristicsInput,
  useAdjacentPesTestQuery,
  useDeletePesTestMutation,
  useMarkTestFailedMutation,
  usePesTestQuery,
  usePesTestIndicatorLimitQuery,
  usePesTestFeatureCacheFeaturesLazyQuery,
  usePurgePesTestFeatureCacheMutation,
  useQueuePesTestPredictionMutation,
  useRecoverPesTestFeatureCacheMutation,
  useRenamePesTestMutation,
  useUpdateExcludeFromMlMutation,
  useUpdatePesTestCharacteristicsMutation,
  useUpdatePesTestLocationMutation,
} from "generated/hooks";
import { openedTestsAtom } from "~/state/openedTests";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { desnake, titleCase } from "@util/string.util";
import { calculateDammChecksum } from "@util/Damm";
import Loading from "@components/loading/Loading";
import { useFetchAllResults } from "~/hooks/useFetchAllResults";
import {
  EditLocationDialog,
  EditLocationDialogSubmitInput,
} from "@pages/admin/pes-tests/dialogs/EditLocationDialog";
import { EditCharacteristicsDialog } from "@pages/admin/pes-tests/dialogs/EditCharacteristicsDialog";
import { MarkFailedDialog } from "@pages/admin/pes-tests/dialogs/MarkFailedDialog";
import { QueuePredictionDialog } from "@pages/admin/pes-tests/dialogs/QueuePredictionDialog";
import { DeletePesTestDialog } from "@pages/admin/pes-tests/dialogs/DeletePesTestDialog";
import { TransferTestDialog } from "@pages/admin/pes-tests/dialogs/TransferTestDialog";
import SensorDataCharts from "./SensorDataCharts";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { CopyButton } from "~/components/ui/copy-button";
import { Spinner } from "~/components/ui/spinner";
import { Input } from "~/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import {
  compareIndicatorLabels,
  getIndicatorLabel,
} from "~/util/indicatorLabel";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import ArrowRightIcon from "@icons/chevron-right.svg?react";
import { Routes, getRoute } from "~/routes";
import { parseServerError } from "~/util/parseError";
import { TestNameEditor } from "./TestNameEditor";

type ProcessedResultsRow = {
  indicatorId: string;
  indicatorLabel: string;
  values: Partial<Record<string, number>>;
};

type FeatureInspectorSelection = {
  name: string;
  displayValue: string;
  hasValue: boolean;
  value?: number | null;
};

export const PesTestDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const recoveredTestIdsRef = useRef(new Set<string>());
  const { loading, error, data, fetchMore, refetch } = usePesTestQuery({
    variables: { id: id! },
    skip: !id,
    pollInterval: 5000,
  });
  const test = data?.pesTest;
  // Staff-only field, fetched separately so the shared PesTest query stays
  // usable by customer-role viewers.
  const { data: indicatorLimitData } = usePesTestIndicatorLimitQuery({
    variables: { id: id! },
    skip: !id,
  });
  const allowedIndicatorDefinitions =
    indicatorLimitData?.pesTest?.allowedIndicatorDefinitions ?? null;
  const [fetchFeatureMatches, featureMatchesResult] =
    usePesTestFeatureCacheFeaturesLazyQuery({
      fetchPolicy: "network-only",
    });
  useFetchAllResults(fetchMore, test?.results);
  const [editMode, setEditMode] = useState<
    | "none"
    | "location"
    | "characteristics"
    | "markFailed"
    | "queuePrediction"
    | "delete"
    | "transfer"
    | "purgeFeatureCache"
  >("none");
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [featureSearch, setFeatureSearch] = useState("");
  const [featureSearchOpen, setFeatureSearchOpen] = useState(false);
  const [featureMatchesCacheRevision, setFeatureMatchesCacheRevision] =
    useState<string | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<
    FeatureInspectorSelection[]
  >([]);
  const [updatePesTestLocation, updatePesTestLocationResult] =
    useUpdatePesTestLocationMutation();
  const [deletePesTest, deletePesTestResult] = useDeletePesTestMutation();
  const [updateExcludeFromML] = useUpdateExcludeFromMlMutation();
  const [renamePesTest] = useRenamePesTestMutation();
  const [updatePesTestCharacteristics, updatePesTestCharacteristicsResult] =
    useUpdatePesTestCharacteristicsMutation();
  const [markTestFailed, markTestFailedResult] = useMarkTestFailedMutation();
  const [queuePrediction, queuePredictionResult] =
    useQueuePesTestPredictionMutation();
  const [recoverFeatureCache] = useRecoverPesTestFeatureCacheMutation();
  const [purgeFeatureCache, purgeFeatureCacheResult] =
    usePurgePesTestFeatureCacheMutation();
  const [processedResultsOpen, setProcessedResultsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [, setOpenedTests] = useAtom(openedTestsAtom);

  // Prev/Next navigation: find adjacent tests by utcTestTime
  const { data: prevData } = useAdjacentPesTestQuery({
    variables: {
      where: test?.utcTestTime
        ? { utcTestTime: { lt: test.utcTestTime } }
        : undefined,
      order: [{ utcTestTime: SortEnumType.Desc }],
    },
    skip: !test?.utcTestTime,
  });

  const { data: nextData } = useAdjacentPesTestQuery({
    variables: {
      where: test?.utcTestTime
        ? { utcTestTime: { gt: test.utcTestTime } }
        : undefined,
      order: [{ utcTestTime: SortEnumType.Asc }],
    },
    skip: !test?.utcTestTime,
  });

  const prevTestId = prevData?.pesTests?.nodes?.[0]?.id;
  const nextTestId = nextData?.pesTests?.nodes?.[0]?.id;

  // Mark test as viewed when the page loads
  useEffect(() => {
    if (id) {
      setOpenedTests((prev) => ({
        ...prev,
        [id]: { openedAt: Date.now() },
      }));
    }
  }, [id, setOpenedTests]);

  useEffect(() => {
    if (!id || recoveredTestIdsRef.current.has(id)) {
      return;
    }

    recoveredTestIdsRef.current.add(id);
    void recoverFeatureCache({
      variables: { testId: id },
    })
      .then((result) => {
        if (result.data?.recoverPesTestFeatureCache) {
          return refetch();
        }
      })
      .catch(() => {
        // Recovery is best-effort; regular polling keeps the page current.
      });
  }, [id, recoverFeatureCache, refetch]);

  const handleNavigate = (testId: string) => {
    setOpenedTests((prev) => ({
      ...prev,
      [testId]: { openedAt: Date.now() },
    }));
    navigate(`/admin/pes-tests/${testId}`);
  };

  const processedResultsData = useMemo(() => {
    const results = test?.results?.nodes ?? [];

    if (!results.length) {
      return {
        models: [] as string[],
        rows: [] as ProcessedResultsRow[],
        latestUpdatedAtByModel: {} as Partial<Record<string, Date>>,
      };
    }

    const models = Array.from(
      new Set(
        results.map((result) => result.indicator?.model?.name ?? "Unknown")
      )
    ).sort((a, b) => a.localeCompare(b));

    const rowsMap = new Map<
      string,
      { indicatorLabel: string; values: Partial<Record<string, number>> }
    >();
    const latestUpdatedAtByModel: Partial<Record<string, Date>> = {};

    results.forEach((result) => {
      const modelName = result.indicator?.model?.name ?? "Unknown";
      const updatedAt = new Date(result.updatedAt);
      const latestUpdatedAt = latestUpdatedAtByModel[modelName];

      if (!latestUpdatedAt || updatedAt > latestUpdatedAt) {
        latestUpdatedAtByModel[modelName] = updatedAt;
      }

      const definition = result.indicator?.indicatorDefinition;
      if (!definition) {
        return;
      }
      const indicatorId = String(definition.id);
      const indicatorLabel = definition.displayName ?? definition.name;

      if (!rowsMap.has(indicatorId)) {
        rowsMap.set(indicatorId, {
          indicatorLabel,
          values: {},
        });
      }

      rowsMap.get(indicatorId)!.values[modelName] = result.value;
    });

    const rows = Array.from(rowsMap.entries())
      .map(([indicatorId, entry]) => ({
        indicatorId,
        indicatorLabel: entry.indicatorLabel,
        values: entry.values,
      }))
      .sort((a, b) => a.indicatorLabel.localeCompare(b.indicatorLabel));

    return { models, rows, latestUpdatedAtByModel };
  }, [test?.results?.nodes]);

  const handleDownload = async () => {
    if (!test?.sampleFileUrl || isDownloading) {
      return;
    }

    setDownloadError(null);
    setIsDownloading(true);
    try {
      const csvUrl = test.sampleFileUrl.replace(".json", ".csv");

      const response = await fetch(csvUrl, { credentials: "same-origin" });
      if (!response.ok) {
        throw new Error(`Failed to download CSV: ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "sensor_data.csv";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      setDownloadError("Unable to download CSV right now. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEditLocation = async ({
    latitude,
    longitude,
  }: EditLocationDialogSubmitInput) => {
    setFormError(null);
    const result = await updatePesTestLocation({
      variables: {
        input: {
          testId: test!.id,
          longitude,
          latitude,
        },
      },
      refetchQueries: [{ query: PesTestDocument, variables: { id: id! } }],
    });
    if (result.data?.updatePesTestLocation) {
      setEditMode("none");
    }
  };

  const handleEditCharacteristics = async (
    moisture: MoistureLevel,
    season: Season,
    texture: SimpleSoilTexture,
    cropTypeId: string,
    isReferenceZone: boolean
  ) => {
    const input: UpdatePesTestCharacteristicsInput & {
      isReferenceZone: boolean;
    } = {
      testId: test!.id,
      moisture,
      season,
      soilTexture: texture,
      cropTypeId,
      isReferenceZone,
    };

    const result = await updatePesTestCharacteristics({
      variables: {
        input,
      },
      refetchQueries: [{ query: PesTestDocument, variables: { id: id! } }],
    });
    if (result.data?.updatePesTestCharacteristics) {
      setEditMode("none");
    }
  };

  const handleMarkFailed = async (reasons: string[]) => {
    if (!test?.id) {
      return;
    }
    const result = await markTestFailed({
      variables: {
        input: {
          testId: test.id,
          failureReasons: reasons,
        },
      },
      refetchQueries: [{ query: PesTestDocument, variables: { id: id! } }],
    });
    if (result.data?.markTestFailed) {
      setEditMode("none");
    }
  };

  const handleClearFailureReasons = async () => {
    if (!test?.id) {
      return;
    }
    const result = await markTestFailed({
      variables: {
        input: {
          testId: test.id,
          failureReasons: [],
        },
      },
      refetchQueries: [{ query: PesTestDocument, variables: { id: id! } }],
    });
    if (result.data?.markTestFailed) {
      setEditMode("none");
    }
  };

  const handleQueuePrediction = async (
    pipeline: PredictionPipeline,
    refreshCache: boolean
  ) => {
    if (!test?.id) {
      return;
    }
    const result = await queuePrediction({
      variables: {
        input: {
          testId: test.id,
          pipeline,
          refreshCache,
        },
      },
    });
    if (result.data?.queuePesTestPrediction) {
      setEditMode("none");
    }
  };

  const handlePurgeFeatureCache = async () => {
    if (!test?.id || !id) {
      return;
    }

    try {
      const result = await purgeFeatureCache({
        variables: {
          input: {
            testId: test.id,
            reason: "Purged from admin test details page.",
          },
        },
        refetchQueries: [{ query: PesTestDocument, variables: { id } }],
      });

      const purgedCount =
        result.data?.purgePesTestFeatureCache.purgedCount ?? 0;
      toast.success(
        purgedCount === 1
          ? "Feature cache purged."
          : `${purgedCount} feature caches purged.`
      );
      setEditMode("none");
    } catch (error) {
      toast.error(
        `Unable to purge feature cache: ${parseServerError(error).message}`
      );
    }
  };

  const handleDeleteTest = async (reason: string) => {
    if (!test?.id) {
      return;
    }

    setDeleteError(null);

    try {
      const result = await deletePesTest({
        variables: {
          input: {
            testId: test.id,
            reason,
          },
        },
      });

      if (!result.data?.deletePESTest) {
        setDeleteError("Unable to delete test right now. Please try again.");
        return;
      }

      toast.success("Test deleted.");
      setEditMode("none");
      navigate(Routes.PesTests);
    } catch (error) {
      setDeleteError(
        `Unable to delete test: ${parseServerError(error).message}`
      );
    }
  };

  const handleTransferSuccess = async () => {
    toast.success("Test transferred.");
    setEditMode("none");
    await refetch();
  };

  const handleRenameTest = async (name: string) => {
    if (!test?.id || !id) {
      return false;
    }

    try {
      const result = await renamePesTest({
        variables: {
          input: {
            testId: test.id,
            name,
          },
        },
        refetchQueries: [{ query: PesTestDocument, variables: { id } }],
      });

      if (!result.data?.renamePesTest) {
        toast.error("Unable to rename test. Please try again.");
        return false;
      }

      toast.success(
        test.runId
          ? "Test name updated for all tests in this run."
          : "Test name updated."
      );
      return true;
    } catch (error) {
      toast.error(`Unable to rename test: ${parseServerError(error).message}`);
      return false;
    }
  };

  const troUuid = test?.troUuid;
  const troName = test?.tro?.name ?? "Unknown";
  const truncatedTroUuid = troUuid ? `0x${troUuid.slice(-12)}` : null;

  const cassetteSerialNumber = useMemo(() => {
    const serialNumber = test?.cassette?.serialNumber?.serialNumber;

    if (serialNumber === null || serialNumber === undefined) {
      return null;
    }

    const productCode =
      test?.cassette?.serialNumber?.range?.productType?.code ?? "";
    const full = `${productCode}${serialNumber.toString().padStart(7, "0")}`;

    return `${full}${calculateDammChecksum(full)}`;
  }, [test?.cassette?.serialNumber]);

  // Trimmed once, so the link text and the clipboard cannot disagree and a
  // whitespace-only name is treated as no name at all.
  const eruName = test?.eru?.name?.trim() || null;

  const processedResultsContentId = `processed-results-${test?.id ?? "content"}`;
  const hasProcessedResults = processedResultsData.rows.length > 0;
  const featureCaches = test?.featureCaches ?? [];
  const latestFeatureCache = featureCaches[0];
  const latestFeatureCacheId = latestFeatureCache?.id;
  const latestFeatureCacheWarnings =
    latestFeatureCache?.warningSummary?.messages ?? [];
  const featureCacheCount = featureCaches.reduce(
    (total, cache) => total + cache.featureCount,
    0
  );
  const latestFeatureCacheRevision = latestFeatureCache
    ? `${latestFeatureCache.id}:${latestFeatureCache.updatedAt}:${latestFeatureCache.sourceUri}`
    : null;
  const featureMatches =
    featureMatchesCacheRevision === latestFeatureCacheRevision
      ? featureMatchesResult.data?.pesTest?.featureCaches.find(
          (cache) => cache.id === latestFeatureCacheId
        )?.featureMatches ?? []
      : [];

  useEffect(() => {
    setFeatureSearch("");
    setFeatureSearchOpen(false);
    setSelectedFeatures([]);
    setFeatureMatchesCacheRevision(null);
  }, [latestFeatureCacheRevision]);

  useEffect(() => {
    const trimmedSearch = featureSearch.trim();
    if (!id || !latestFeatureCacheId || trimmedSearch.length < 2) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setFeatureMatchesCacheRevision(latestFeatureCacheRevision);
      fetchFeatureMatches({
        variables: {
          id,
          search: trimmedSearch,
          limit: 12,
        },
      });
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [
    featureSearch,
    fetchFeatureMatches,
    id,
    latestFeatureCacheId,
    latestFeatureCacheRevision,
  ]);

  return (
    <PageTemplate
      title={
        test ? (
          <TestNameEditor name={test.name} onSubmit={handleRenameTest} />
        ) : (
          "View Test"
        )
      }
      browserTitle={test?.name ?? "View Test"}
      subtitle="Test Details"
      activeItem="Admin"
      activeSubItem="Test Files"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "Test Files", path: "/admin/pes-tests" },
        {
          label: test ? test.name : "View Test",
          path: `/admin/pes-tests/${test?.id ?? ""}`,
        },
      ]}
      actions={[
        {
          text: "Previous",
          action: () => {
            if (prevTestId) {
              handleNavigate(prevTestId);
            }
          },
          variant: "secondary",
          icon: <ChevronLeft className="size-4" />,
          disabled: !prevTestId,
        },
        {
          text: "Next",
          action: () => {
            if (nextTestId) {
              handleNavigate(nextTestId);
            }
          },
          variant: "secondary",
          icon: <ChevronRight className="size-4" />,
          disabled: !nextTestId,
        },
      ]}
    >
      {loading && !test && <Loading />}
      {error && !test && <div>Error loading test: {error.message}</div>}
      {test && (
        <>
          <Card className="w-full gap-3 py-4">
            <CardHeader className="pb-2">
              <CardTitle>Test Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {allowedIndicatorDefinitions ? (
                <Alert data-testid="indicator-limit-note">
                  <ShieldAlert aria-hidden="true" />
                  <AlertTitle>Customer indicator limit active</AlertTitle>
                  <AlertDescription>
                    <p>
                      Staff see every indicator below, but this test&apos;s
                      customer users (and CSV/PDF exports) only see:{" "}
                      {allowedIndicatorDefinitions.length > 0
                        ? allowedIndicatorDefinitions
                            .map(getIndicatorLabel)
                            .sort(compareIndicatorLabels)
                            .join(", ")
                        : "no indicators"}
                      .
                    </p>
                  </AlertDescription>
                </Alert>
              ) : null}
              {troUuid && truncatedTroUuid && (
                <div className="-mt-2 flex flex-wrap items-center gap-0 text-xs font-semibold text-muted-foreground">
                  <span>{troName} (</span>
                  <span className="rounded-md border border-input bg-muted px-0 m-0 py-0.5 font-mono text-xs font-medium text-foreground">
                    {truncatedTroUuid}
                  </span>
                  <CopyButton value={troUuid} label="TRO UUID" />
                  <span>)</span>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <section className="space-y-1 text-sm">
                  <p className="text-sm font-semibold text-foreground">
                    Test Info
                  </p>
                  <p className="text-muted-foreground">
                    Uploaded by: {displayUserName(test.createdBy)}
                  </p>
                  <p className="text-muted-foreground">
                    Test Type: {titleCase(desnake(test.testType))}
                  </p>
                  <p className="text-muted-foreground">
                    Test Time:{" "}
                    {format(new Date(test.utcTestTime), "dd/MM/yyyy HH:mm")}
                  </p>
                  <p className="text-muted-foreground">
                    Uploaded At:{" "}
                    {format(new Date(test.createdAt), "dd/MM/yyyy HH:mm")}
                  </p>
                  <p className="text-muted-foreground">
                    Failure: {test.isFailure ? "Yes" : "No"}
                  </p>
                  <p className="text-muted-foreground">
                    Is Processed: {test.sampleFileUrl ? "Yes" : "No"}
                  </p>
                  <p className="text-muted-foreground">
                    Excluded from ML:{" "}
                    {!test.soilSample || test.excludeFromMl ? "Yes" : "No"}
                  </p>
                </section>
                <section className="space-y-1 text-sm">
                  <p className="text-sm font-semibold text-foreground">
                    Sample Characteristics
                  </p>
                  <p className="text-muted-foreground">
                    Moisture: {titleCase(test.moisture)}
                  </p>
                  <p className="text-muted-foreground">
                    Season: {titleCase(test.season)}
                  </p>
                  <p className="text-muted-foreground">
                    Soil Texture: {titleCase(test.soilTexture)}
                  </p>
                  <p className="text-muted-foreground">
                    Crop Type: {test.displayCropTypeName ?? "Unknown"}
                  </p>
                  <p className="text-muted-foreground">
                    Is reference zone: {test.isReferenceZone ? "Yes" : "No"}
                  </p>
                </section>
                <section className="space-y-1 text-sm">
                  <p className="text-sm font-semibold text-foreground">
                    Model &amp; Equipment
                  </p>
                  {test.originalModelName && (
                    <p className="text-muted-foreground">
                      ML Model Name: {test.originalModelName}
                    </p>
                  )}
                  {test.cassette?.serialNumber && cassetteSerialNumber ? (
                    <div className="flex items-center gap-1">
                      <Link
                        to={getRoute(Routes.AdminCassetteDetails, {
                          id: test.cassette.id,
                        })}
                        className="text-sm font-medium !text-cyan-400 underline-offset-4 transition hover:!text-cyan-300 hover:underline"
                      >
                        Cassette SN: {cassetteSerialNumber}
                      </Link>
                      <CopyButton
                        value={cassetteSerialNumber}
                        label="serial number"
                      />
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      Cassette SN: Not available
                    </p>
                  )}
                  {test.eru && eruName && (
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/admin/pes-tests?erus=${test.eru.id}`}
                        className="text-sm font-medium !text-cyan-400 underline-offset-4 transition hover:!text-cyan-300 hover:underline"
                      >
                        ERU Name: {eruName}
                      </Link>
                      <CopyButton value={eruName} label="ERU name" />
                    </div>
                  )}
                </section>
                <section className="space-y-1 text-sm">
                  <p className="text-sm font-semibold text-foreground">
                    Test Location
                  </p>
                  <p className="text-muted-foreground">
                    Organisation: {test.organisation?.name ?? "Unknown"}
                  </p>
                  <p className="text-muted-foreground">
                    Farm: {test.farm?.name ?? "Unknown"}
                  </p>
                  <p className="text-muted-foreground">
                    Field: {test.field?.name ?? "Unknown"}
                  </p>
                  {test.location && (
                    <>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${test.soilSample?.location?.latitude ?? test.location.latitude},${test.soilSample?.location?.longitude ?? test.location.longitude}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-sm font-medium !text-cyan-400 underline-offset-4 transition hover:!text-cyan-300 hover:underline"
                      >
                        GPS:{" "}
                        {`${(
                          test.soilSample?.location?.latitude ??
                          test.location.latitude
                        ).toFixed(4)}, ${(
                          test.soilSample?.location?.longitude ??
                          test.location.longitude
                        ).toFixed(4)}`}
                      </a>
                      {(test.soilSample?.location?.what3Words?.words ||
                        test.location?.what3Words?.words) && (
                        <p className="text-muted-foreground">
                          What3Words:{" "}
                          {test.soilSample?.location?.what3Words?.words ??
                            test.location?.what3Words?.words}
                        </p>
                      )}
                    </>
                  )}
                </section>
                <section className="space-y-2 text-sm">
                  <p className="text-sm font-semibold text-foreground">
                    File details
                  </p>
                  {test.sampleFileUrl && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <>
                          <Spinner className="mr-2" />
                          Downloading...
                        </>
                      ) : (
                        "Download CSV"
                      )}
                    </Button>
                  )}
                  {downloadError && (
                    <p className="text-sm text-destructive">{downloadError}</p>
                  )}
                  {test.soilSample && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground">Soil Sample:</p>
                      <Link
                        to={`/admin/soil-samples/${test.soilSample.id}`}
                        className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                      >
                        {test.soilSample.name || test.soilSample.id}
                      </Link>
                    </div>
                  )}
                </section>
                <section className="space-y-2 text-sm sm:col-span-2 lg:col-span-3">
                  <p className="text-sm font-semibold text-foreground">
                    Actions
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-full justify-center px-2 text-xs"
                      onClick={() => setEditMode("location")}
                    >
                      Edit location
                    </Button>
                    {test.soilSample ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex w-full">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-full justify-center px-2 text-xs"
                              disabled
                            >
                              Edit characteristics
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          Cannot edit characteristics when linked to a soil
                          sample
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-full justify-center px-2 text-xs"
                        onClick={() => setEditMode("characteristics")}
                      >
                        Edit characteristics
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-full justify-center px-2 text-xs"
                      onClick={() => setEditMode("markFailed")}
                    >
                      Mark/Unmark as failed
                    </Button>
                    {test.soilSample ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex w-full">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-full justify-center px-2 text-xs"
                              disabled
                            >
                              Transfer test
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          Tests linked to soil samples cannot be transferred
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-full justify-center px-2 text-xs"
                        onClick={() => setEditMode("transfer")}
                      >
                        Transfer test
                      </Button>
                    )}
                    {test.soilSample && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-full justify-center px-2 text-xs"
                        onClick={() => {
                          updateExcludeFromML({
                            variables: {
                              testId: test.id,
                              exclude: !test.excludeFromMl,
                            },
                          });
                        }}
                      >
                        {test.excludeFromMl
                          ? "Include in ML"
                          : "Exclude from ML"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-full justify-center px-2 text-xs"
                      onClick={() => setEditMode("queuePrediction")}
                      disabled={
                        !test.sampleFileUrl ||
                        !test.originalModelName ||
                        queuePredictionResult.loading
                      }
                    >
                      {queuePredictionResult.loading
                        ? "Queuing..."
                        : "Queue for Results Prediction"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-8 w-full justify-center px-2 text-xs"
                      onClick={() => {
                        setDeleteError(null);
                        setEditMode("delete");
                      }}
                    >
                      Delete test
                    </Button>
                  </div>
                </section>
              </div>
            </CardContent>
          </Card>
          <Card className="w-full gap-3 py-4">
            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
              <CardTitle className="flex items-center gap-2">
                <Database className="size-4" aria-hidden="true" />
                Features
              </CardTitle>
              <Button
                size="sm"
                variant="destructive"
                className="h-8 px-2 text-xs"
                onClick={() => setEditMode("purgeFeatureCache")}
                disabled={
                  featureCaches.length === 0 || purgeFeatureCacheResult.loading
                }
              >
                <Trash2 className="mr-1 size-3.5" aria-hidden="true" />
                {purgeFeatureCacheResult.loading ? "Purging..." : "Purge cache"}
              </Button>
            </CardHeader>
            <CardContent>
              {latestFeatureCache ? (
                <div className="space-y-3">
                  <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <section className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Feature Count
                      </p>
                      <p className="text-muted-foreground">
                        {featureCacheCount.toLocaleString()}
                      </p>
                    </section>
                    <section className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Extracted At
                      </p>
                      <p className="text-muted-foreground">
                        {format(
                          new Date(latestFeatureCache.extractedAt),
                          "dd/MM/yyyy HH:mm"
                        )}
                      </p>
                    </section>
                    <section className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Version
                      </p>
                      <p className="text-muted-foreground">
                        {latestFeatureCache.featureSetVersion}
                      </p>
                    </section>
                    <section className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Source
                      </p>
                      <p className="break-all font-mono text-xs text-muted-foreground">
                        {latestFeatureCache.sourceUri}
                      </p>
                    </section>
                  </div>
                  <section className="space-y-2 border-t pt-3">
                    <p className="text-sm font-semibold text-foreground">
                      Feature Inspector
                    </p>
                    <div className="relative max-w-xl">
                      <Input
                        value={featureSearch}
                        placeholder="Search feature name"
                        autoComplete="off"
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setFeatureSearch(nextValue);
                          setFeatureSearchOpen(nextValue.trim().length >= 2);
                        }}
                        onFocus={() => {
                          setFeatureSearchOpen(featureSearch.trim().length >= 2);
                        }}
                        onBlur={() => {
                          window.setTimeout(() => setFeatureSearchOpen(false), 100);
                        }}
                      />
                      {featureSearchOpen && featureSearch.trim().length >= 2 && (
                        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover p-1 text-sm text-popover-foreground shadow-md">
                          {featureMatchesResult.loading ? (
                            <div className="px-2 py-2 text-muted-foreground">
                              Searching...
                            </div>
                          ) : featureMatchesResult.error ? (
                            <div className="px-2 py-2 text-destructive">
                              Unable to load features.
                            </div>
                          ) : featureMatches.length === 0 ? (
                            <div className="px-2 py-2 text-muted-foreground">
                              No matching features.
                            </div>
                          ) : (
                            featureMatches.map((feature) => (
                              <button
                                key={feature.name}
                                type="button"
                                className="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-hidden"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                  setSelectedFeatures((current) => {
                                    const withoutExisting = current.filter(
                                      (item) => item.name !== feature.name
                                    );
                                    return [...withoutExisting, feature];
                                  });
                                  setFeatureSearch("");
                                  setFeatureSearchOpen(false);
                                }}
                              >
                                <span className="min-w-0 truncate font-mono text-xs">
                                  {feature.name}
                                </span>
                                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                                  {feature.displayValue}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {selectedFeatures.length > 0 && (
                      <div className="w-full overflow-hidden rounded-md border bg-muted/30 text-sm">
                        <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
                          <p className="text-xs font-semibold uppercase text-muted-foreground">
                            Selected features
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => setSelectedFeatures([])}
                          >
                            Clear
                          </Button>
                        </div>
                        <div className="divide-y">
                          {selectedFeatures.map((feature) => (
                            <div
                              key={feature.name}
                              className="grid gap-2 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_minmax(120px,auto)_auto] sm:items-center"
                            >
                              <p className="min-w-0 break-all font-mono text-xs text-foreground">
                                {feature.name}
                              </p>
                              <p className="font-mono text-sm font-semibold text-foreground sm:text-right">
                                {feature.displayValue}
                              </p>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-7 justify-self-start sm:justify-self-end"
                                aria-label={`Remove ${feature.name}`}
                                onClick={() => {
                                  setSelectedFeatures((current) =>
                                    current.filter(
                                      (item) => item.name !== feature.name
                                    )
                                  );
                                }}
                              >
                                <X className="size-3.5" aria-hidden="true" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                  {latestFeatureCache.warningSummary?.hasWarnings && (
                    <Alert variant="destructive">
                      <AlertTriangle className="size-4" aria-hidden="true" />
                      <AlertTitle>Partial feature set</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc space-y-1 pl-4">
                          {latestFeatureCacheWarnings.map((message) => (
                            <li key={message}>{message}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No extracted feature cache is stored for this test yet.
                  Checking for updates...
                </p>
              )}
            </CardContent>
          </Card>
          {test.failureReasons && test.failureReasons.length > 0 && (
            <Card className="w-full gap-3 py-4">
              <CardHeader className="pb-2">
                <CardTitle>Test Issues</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2">
                  This test has the following issues:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {test.failureReasons.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {hasProcessedResults && (
            <Collapsible
              open={processedResultsOpen}
              onOpenChange={setProcessedResultsOpen}
            >
              <Card className="w-full gap-2.5 py-4">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Processed Results</CardTitle>
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-expanded={processedResultsOpen}
                      aria-controls={processedResultsContentId}
                    >
                      <ArrowRightIcon
                        className={cn(
                          "size-4 transition-transform",
                          processedResultsOpen && "rotate-90"
                        )}
                        aria-hidden="true"
                      />
                      <span className="sr-only">
                        {processedResultsOpen ? "Collapse" : "Expand"} processed
                        results
                      </span>
                    </Button>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="pt-0" id={processedResultsContentId}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Indicator</TableHead>
                          {processedResultsData.models.map((modelName) => {
                            const latestUpdatedAt =
                              processedResultsData.latestUpdatedAtByModel[
                                modelName
                              ];

                            return (
                              <TableHead key={modelName}>
                                <span className="block">{modelName}</span>
                                <span className="block text-xs font-normal text-muted-foreground">
                                  {latestUpdatedAt
                                    ? `Last analysed: ${format(latestUpdatedAt, "d MMM yyyy, HH:mm")}`
                                    : "Not yet analysed"}
                                </span>
                              </TableHead>
                            );
                          })}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {processedResultsData.rows.map((row) => (
                          <TableRow key={row.indicatorId}>
                            <TableCell className="font-medium">
                              {row.indicatorLabel}
                            </TableCell>
                            {processedResultsData.models.map((modelName) => {
                              const value = row.values[modelName];
                              const cellText =
                                value === undefined
                                  ? "-"
                                  : value.toLocaleString(undefined, {
                                      maximumFractionDigits: 4,
                                    });
                              return (
                                <TableCell
                                  key={`${row.indicatorId}-${modelName}`}
                                  className={
                                    value === undefined
                                      ? "text-muted-foreground"
                                      : undefined
                                  }
                                >
                                  {cellText}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
          {test.sampleFileUrl && (
            <Card className="w-full gap-3 py-4">
              <CardHeader className="items-center pb-1.5 text-center">
                <CardTitle>Sensor Data</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <SensorDataCharts
                  tests={[
                    {
                      sampleFileUrl: test.sampleFileUrl,
                      name: `${test.name || test.id}`,
                    },
                  ]}
                />
              </CardContent>
            </Card>
          )}
          <EditLocationDialog
            isOpen={editMode === "location"}
            onCancel={() => setEditMode("none")}
            onConfirm={handleEditLocation}
            error={formError || updatePesTestLocationResult.error?.message}
            loading={updatePesTestLocationResult.loading}
            defaultLongitude={test.location?.longitude?.toString() ?? ""}
            defaultLatitude={test.location?.latitude?.toString() ?? ""}
            defaultWhat3Words={test.location?.what3Words?.words ?? ""}
          />
          <EditCharacteristicsDialog
            isOpen={editMode === "characteristics"}
            onCancel={() => setEditMode("none")}
            onConfirm={handleEditCharacteristics}
            defaultMoisture={test.moisture}
            defaultSeason={test.season}
            defaultTexture={test.soilTexture}
            defaultCropType={test.cropType ?? { id: "" }}
            defaultIsReferenceZone={test.isReferenceZone}
            error={updatePesTestCharacteristicsResult.error?.message}
          />
          <MarkFailedDialog
            isOpen={editMode === "markFailed"}
            onCancel={() => setEditMode("none")}
            onSubmit={handleMarkFailed}
            onClear={handleClearFailureReasons}
            currentReasons={test.failureReasons ?? []}
            error={markTestFailedResult.error?.message}
            loading={markTestFailedResult.loading}
          />
          <QueuePredictionDialog
            isOpen={editMode === "queuePrediction"}
            onCancel={() => setEditMode("none")}
            onConfirm={handleQueuePrediction}
            error={queuePredictionResult.error?.message}
            loading={queuePredictionResult.loading}
          />
          <TransferTestDialog
            isOpen={editMode === "transfer"}
            test={test}
            onCancel={() => setEditMode("none")}
            onTransferred={handleTransferSuccess}
          />
          <DeletePesTestDialog
            isOpen={editMode === "delete"}
            isFailure={test.isFailure}
            onCancel={() => setEditMode("none")}
            onConfirm={handleDeleteTest}
            error={deleteError}
            loading={deletePesTestResult.loading}
          />
          <AlertDialog
            open={editMode === "purgeFeatureCache"}
            onOpenChange={(open) => {
              if (!open && !purgeFeatureCacheResult.loading) {
                setEditMode("none");
              }
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Purge feature cache</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the cached feature payload for this test. The
                  next prediction run will need to extract features again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {purgeFeatureCacheResult.error && (
                <p className="text-sm text-destructive">
                  {parseServerError(purgeFeatureCacheResult.error).message}
                </p>
              )}
              <AlertDialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditMode("none")}
                  disabled={purgeFeatureCacheResult.loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handlePurgeFeatureCache}
                  disabled={purgeFeatureCacheResult.loading}
                >
                  {purgeFeatureCacheResult.loading
                    ? "Purging..."
                    : "Purge cache"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </PageTemplate>
  );
};

export default PesTestDetails;
